import { CONTAINER_TYPE } from 'khufu-legos';
import { useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import SubmitButton from '../../../components/Buttons/SubmitButton';
import { useAxiosMemoized } from '../../../hooks/useAxios';
import { Product } from '../../../types';
import DisplayFieldErrors from '../../../utils/DisplayFormError';
import { formatDateTime } from '../../../utils/formatDateTime';

interface UpdateProductFormInput {
  name: string;
  code: string;
  description: string;
  price: number;
  qty: string;
  stockAtHand: string;
  isDiscount: boolean;
  discount: number;
  isActive: boolean;
  requiresEmpties: boolean;
  containerType: CONTAINER_TYPE;
}

const ProductDetailForm = ({
  product,
  isEditMode,
  onEditFormSubmit
}: {
  product: Product;
  isEditMode: boolean;
  isEditFormSubmitting: boolean;
  onEditFormSubmit: (value: boolean) => void;
}) => {
  const {
    _id,
    name,
    code,
    price,
    description,
    qty,
    isDiscount,
    discount,
    stockAtHand,
    isActive,
    createdAt,
    updatedAt,
    requiresEmpties,
    containerType
  } = product;

  const [executeRequest, { data: updatedProduct, loading }] = useAxiosMemoized<Product>();

  useEffect(() => {
    onEditFormSubmit(loading);
  }, [loading]);

  useEffect(() => {
    if (updatedProduct._id) {
      window.location.reload();
    }
  }, [updatedProduct._id]);

  const methods = useForm<UpdateProductFormInput>({
    mode: 'onBlur',
    defaultValues: {
      name,
      code,
      price,
      description,
      isDiscount,
      discount: discount ?? 0,
      isActive,
      requiresEmpties,
      containerType
    }
  });

  const {
    register,
    formState: { isValid, errors, isDirty },
    getValues,
    setValue,
    watch,
    reset
  } = methods;

  const watchContainerType = watch('containerType');
  useEffect(() => {
    if (watchContainerType === 'can') {
      setValue('requiresEmpties', false);
    }
  }, [watchContainerType, setValue]);

  const onSubmit = async () => {
    const fields = getValues();
    const productData = {
      ...fields,
      description: fields.description && fields.description.trim() ? fields.description : undefined
    };
    executeRequest({ method: 'put', url: `/product/${_id}`, data: { ...productData } });
  };

  const disabled = loading || !isEditMode;

  const createdBy = product.createdBy.fullName ?? '';

  const handleCancel = () => {
    reset();
  };

  return (
    <Form>
      <Form.Group className="my-4">
        <Form.Check
          className="fw-bold"
          type="switch"
          label={'Activate Product'}
          {...register('isActive', { disabled })}
        />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Coke 50cl bottle"
          {...register('name', {
            required: 'Name is required',
            validate: value => value.trim() !== '' || 'Product name cannot be empty',
            pattern: {
              value: /^[a-zA-Z0-9 ]+$/,
              message: 'Product name should contain only alphabets'
            },
            disabled,
            minLength: { value: 3, message: 'Name must be atleast 3 characters' }
          })}
          autoFocus
        />
        {errors?.name && <DisplayFieldErrors displayText={errors.name.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label htmlFor="containerType">Container Type</Form.Label>
        <Form.Select id="containerType" {...register('containerType', { required: true, disabled })}>
          <option value="can">Can</option>
          <option value="bottle">Bottle - Glass / Plastic</option>
        </Form.Select>
      </Form.Group>
      <Form.Group className="my-4">
        <Form.Check
          className="fw-bold"
          type="switch"
          label={'Requires Empties'}
          {...register('requiresEmpties', { disabled: watchContainerType === 'can' || disabled })}
        />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Code</Form.Label>
        <Form.Control
          type="text"
          placeholder="CK50BL"
          {...register('code', {
            required: 'Code is required',
            validate: value => value.trim() !== '' || 'Product code cannot be empty',
            disabled
          })}
        />
        {errors?.code && <DisplayFieldErrors displayText={errors.code.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          type="text"
          placeholder="Type here"
          {...register('description', {
            required: false,
            validate: value => {
              if (value === '') {
                return true;
              }

              const trimmedValue = value?.trim();
              return !!trimmedValue || 'Should not contain only empty spaces';
            },
            pattern: {
              value: /^[a-zA-Z0-9 ]+$/,
              message: 'Description should contain only alphabets'
            },
            disabled
          })}
        />
        {errors?.description && <DisplayFieldErrors displayText={errors.description.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Price</Form.Label>
        <Form.Control
          type="number"
          placeholder="100.00"
          step="0.01"
          min="0.01"
          {...register('price', {
            required: true,
            valueAsNumber: true,
            disabled,
            validate: {
              positive: value => value >= 0.01 || 'Price must be greater than 0',
              decimals: value => /^\d+(\.\d{1,2})?$/.test(String(value)) || 'Price can only accept 2 decimal places'
            }
          })}
        />
        {errors?.price && <DisplayFieldErrors displayText={errors.price.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Qty</Form.Label>
        <Form.Control type="number" disabled={true} value={qty} />
        {errors?.qty && <DisplayFieldErrors displayText={errors.qty.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Avail Stock</Form.Label>
        <Form.Control type="text" placeholder="Avail. Stock" value={stockAtHand} disabled={true} />
        {errors?.stockAtHand && <DisplayFieldErrors displayText={errors.stockAtHand.message!} />}
      </Form.Group>

      <Form.Group className="my-4">
        <Form.Check
          className="fw-bold"
          type="switch"
          label={'Activate Discount'}
          {...register('isDiscount', { disabled })}
        />
      </Form.Group>

      <Form.Group className="my-3">
        <Form.Label>Discount</Form.Label>
        <Form.Control
          type="number"
          placeholder="Discount %"
          {...register('discount', {
            valueAsNumber: true,
            disabled,
            min: { value: 0, message: 'Discount must be greater than 0%' },
            max: { value: 100, message: 'Discount must be less than 100%' }
          })}
        />
        {errors?.discount && <DisplayFieldErrors displayText={errors.discount.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Created By</Form.Label>
        <Form.Control type="text" placeholder="Created By" disabled={true} value={createdBy.toString()} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Created At</Form.Label>
        <Form.Control type="text" placeholder="Created At" disabled={true} value={formatDateTime(createdAt)} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Last Updated</Form.Label>
        <Form.Control type="text" placeholder="Updated At" disabled={true} value={formatDateTime(updatedAt)} />
      </Form.Group>

      {isEditMode && (
        <Row className="my-5">
          <Col>
            <SubmitButton
              type="button"
              buttonText="cancel"
              variant="white"
              onClick={handleCancel}
              disabled={disabled}
              loading={false}
            />
          </Col>
          <Col>
            <SubmitButton
              type="submit"
              buttonText="Submit"
              onClick={onSubmit}
              loading={loading}
              disabled={!isDirty || !isValid || disabled}
            />
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default ProductDetailForm;
